import type { SurveyAnswerRecord } from '@coorum-research/shared-common/types/answer.type'
import { useThrottledCallback } from '@react-hookz/web'
import { answerApi } from '../apis/answers.api'
import { mediaFileApi } from '../apis/media-file.api'
import { useAnswerFlowCommon } from './use-answer-flow-common'

type Params = {
  campaignId: string
  surveyId: string
  surveyConfigurationId: string
  onComplete: (nextLauncherContentId: string | null) => void
}

/**
 * アンケート新規回答用hook
 */
export function useAnswerFlowUpdate({ campaignId, surveyId, surveyConfigurationId, onComplete }: Params) {
  const {
    onCompleteQuestion,
    onUploadFiles,
    setCurrentSurveyData,
    getCurrentSurveyData,
    setCurrentMediaFiles,
    getCurrentMediaFiles,
    setCurrentSurveyCompleteDisabled,
    getCurrentSurveyCompleteDisabled,
  } = useAnswerFlowCommon({ campaignId, surveyId, surveyConfigurationId })

  const { updateAnswer } = answerApi()
  const { connectToAnswer } = mediaFileApi()

  /**
   * 答えたanswerをサーバーへ保存 - create
   */
  const onCompleteSurvey = useThrottledCallback(async (answerId: string, nextLauncherContentId: string | null, senderData: SurveyAnswerRecord | null) => {
    if (senderData === null) {
      return
    }

    try {
      // set disabled button state
      setCurrentSurveyCompleteDisabled(true)

      // api call
      const res = await updateAnswer(answerId, {
        surveyConfigurationId,
        surveyAnswer: senderData,
        answerStatus: 'done',
        launcherContentId: nextLauncherContentId,
      })

      const currentMediaFiles = getCurrentMediaFiles()
      // connect media files to answer
      // MEMO: メディアファイルがある場合は、サーバーへ接続する
      // MEMO: res.idがある場合は、サーバーへ接続する
      if ('id' in res && currentMediaFiles.length > 0) {
        await connectToAnswer(res.id, currentMediaFiles)
        setCurrentMediaFiles([]) // if success connect, reset global state
      }

      // if all save success, reset currentSurveyData
      setCurrentSurveyData(null)

      const surveyFormEl = document.getElementById('surveyForm')
      surveyFormEl?.setAttribute('style', 'display:none')

      if (onComplete) {
        onComplete(nextLauncherContentId)
      }
    } catch (e) {
      // reset disabled button state
      // エラー時にはもう一度ボタンを押せるようにする
      setCurrentSurveyCompleteDisabled(false)
    }
  }, [campaignId, surveyId, surveyConfigurationId, onComplete], 300)

  return {
    onCompleteQuestion,
    onUploadFiles,
    onCompleteSurvey,
    setCurrentSurveyData,
    getCurrentSurveyData,
    getCurrentSurveyCompleteDisabled,
  }
}
